import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle, CheckCircle, Mail, ArrowLeft, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = '/api';

const SYMBOL_LOGO = "https://customer-assets.emergentagent.com/job_neuro-care-hub-2/artifacts/zntnld86_Lifely%20Symbol%20AI.png";
const TEXT_LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_neuro-care-hub-2/artifacts/yxqxnzye_ReGenflo%20logo%202%20contrast.png";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center p-6">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-[#4E7A6B]/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-[#4E7A6B]" />
          </div>
          <h1 className="text-2xl font-['Outfit'] font-bold text-white mb-4" data-testid="forgot-password-success">Check Your Email</h1>
          <p className="text-white/60 mb-2">If an account exists for <span className="text-white">{email}</span>, we've sent a password reset link.</p>
          <p className="text-white/40 text-sm mb-8">The link expires in 1 hour.</p>
          <Link to="/login">
            <Button variant="outline" className="border-white/20 text-white hover:bg-white/5" data-testid="back-to-login-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-3 mb-10">
          <img src={SYMBOL_LOGO} alt="Lifely" className="h-12 w-auto" />
          <img src={TEXT_LOGO_LIGHT} alt="ReGenflō" className="h-8 w-auto" />
        </Link>

        <div className="bg-[#131B2B] rounded-xl border border-white/10 p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-['Outfit'] font-bold text-white mb-2" data-testid="forgot-password-title">Forgot Password</h1>
            <p className="text-white/60 text-sm">Enter your email and we'll send you a reset link</p>
          </div>

          {error && (
            <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/20 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-400" data-testid="forgot-password-error">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/80">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-10 bg-[#0B0F19] border-white/10 text-white placeholder:text-white/30 focus:border-[#C89B7B]"
                  required
                  data-testid="forgot-password-email-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full copper-gradient text-[#0B0F19] hover:opacity-90 py-5"
              disabled={loading}
              data-testid="forgot-password-submit-btn"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
              ) : (
                'Send Reset Link'
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-white/50 mt-6">
            Remember your password?{' '}
            <Link to="/login" className="text-[#C89B7B] hover:underline" data-testid="back-to-login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
